/**
 * The one voice the narrator uses on this device, for this language.
 *
 * The browser's own voices cost nothing, and their quality ranges from the
 * neural voices Edge and recent Apple systems ship down to the novelty ones
 * macOS has carried for decades. The pick is the same on every page so the
 * brand sounds like one reader:
 *   1. only voices in the article's language (never English reading Welsh);
 *   2. the best tier the device has (neural and premium first, Google's
 *      remote voices next, the plain system voices after, the old desktop
 *      SAPI voices last, novelty voices never);
 *   3. within a tier, the region the visitor is reading in, then the
 *      language's usual regions;
 *   4. within that, a short list of named voices chosen for a calm, even,
 *      warm read, so two visitors on the same platform hear the same reader.
 *
 * Returns null when the device has no voice in the language at all; the
 * narrator then stays off rather than read the article in another language.
 */

export interface VoiceLike {
  name: string;
  lang: string;
  voiceURI: string;
  localService: boolean;
  default: boolean;
}

/** Apple's novelty and "fun" voices: never a narrator. */
const NOVELTY_NAMES = new Set(
  [
    'albert', 'bad news', 'bahh', 'bells', 'boing', 'bubbles', 'cellos', 'deranged', 'good news', 'hysterical',
    'jester', 'organ', 'pipe organ', 'superstar', 'trinoids', 'whisper', 'wobble', 'zarvox', 'grandma', 'grandpa',
    'eddy', 'flo', 'reed', 'rocko', 'sandy', 'shelley', 'junior', 'ralph', 'kathy', 'fred', 'bruce', 'agnes', 'vicki',
    'victoria', 'princess',
  ],
);

/**
 * Voices picked for the brand's read, per language, most wanted first. Apple,
 * Microsoft (Edge's "Online (Natural)" set) and Chrome's Google voices, all
 * matched on a word of the name so "Ava (Premium)" and "Microsoft Ava Online
 * (Natural) - English (United States)" both count.
 */
const PREFERRED: Record<string, string[]> = {
  en: ['Ava', 'Zoe', 'Samantha', 'Serena', 'Allison', 'Jenny', 'Sonia', 'Clara', 'Libby', 'Aria', 'Google UK English Female', 'Google US English', 'Daniel', 'Tom', 'Evan', 'Ryan', 'Guy'],
  fr: ['Amélie', 'Audrey', 'Aurélie', 'Sylvie', 'Denise', 'Vivienne', 'Marie', 'Google français', 'Thomas', 'Henri', 'Antoine', 'Nicolas'],
  es: ['Mónica', 'Paulina', 'Elvira', 'Dalia', 'Marisol', 'Google español', 'Jorge', 'Diego', 'Alvaro'],
  de: ['Anna', 'Petra', 'Katja', 'Amala', 'Google Deutsch', 'Markus', 'Conrad'],
  it: ['Alice', 'Federica', 'Elsa', 'Isabella', 'Google italiano', 'Luca', 'Diego'],
  'pt-BR': ['Luciana', 'Francisca', 'Thalita', 'Google português do Brasil', 'Felipe', 'Antonio'],
  'pt-PT': ['Joana', 'Catarina', 'Raquel', 'Duarte'],
  pt: ['Luciana', 'Joana', 'Francisca', 'Google português do Brasil'],
  nl: ['Claire', 'Colette', 'Fenna', 'Xander', 'Google Nederlands', 'Maarten'],
  pl: ['Zosia', 'Ewa', 'Zofia', 'Agnieszka', 'Google polski', 'Marek'],
  sv: ['Alva', 'Klara', 'Sofie', 'Hillevi', 'Oskar', 'Mattias'],
  ja: ['Kyoko', 'O-Ren', 'Nanami', 'Google 日本語', 'Otoya', 'Keita'],
  hi: ['Lekha', 'Swara', 'Google हिन्दी', 'Madhur'],
  ta: ['Vani', 'Pallavi', 'Valluvar'],
  te: ['Geeta', 'Shruti', 'Mohan'],
  bn: ['Piya', 'Tanishaa', 'Bashkar'],
  mr: ['Ananya', 'Aarohi', 'Manohar'],
  ca: ['Montserrat', 'Montse', 'Joana', 'Alba', 'Enric'],
  cy: ['Nia', 'Aled'],
  ga: ['Orla', 'Colm'],
};

/** Regions tried after the visitor's own, per language. */
const REGION_FALLBACK: Record<string, string[]> = {
  en: ['US', 'GB', 'CA', 'AU', 'IE', 'NZ', 'IN'],
  fr: ['FR', 'CA', 'BE', 'CH'],
  es: ['ES', 'MX', 'US', 'AR'],
  pt: ['BR', 'PT'],
  de: ['DE', 'AT', 'CH'],
  it: ['IT', 'CH'],
  nl: ['NL', 'BE'],
};

interface LangTag {
  base: string;
  region: string;
}

export function parseLangTag(tag: string | null | undefined): LangTag {
  const [base = '', region = ''] = (tag || '').replace(/_/g, '-').split('-');
  return { base: base.toLowerCase(), region: region.toUpperCase() };
}

const word = (name: string) => name.toLowerCase().normalize('NFKD').replace(/[̀-ͯ]/g, '');

function isNovelty(voice: VoiceLike): boolean {
  const first = word(voice.name).split(/[\s(]+/)[0];
  return NOVELTY_NAMES.has(first) || NOVELTY_NAMES.has(word(voice.name));
}

/**
 * 5 neural or premium, 4 enhanced, 3 Google's remote voices, 2 the plain
 * system voices, 1 the old Microsoft desktop voices, 0 eSpeak and the like.
 */
export function voiceTier(voice: VoiceLike): number {
  const name = voice.name.toLowerCase();
  const uri = voice.voiceURI.toLowerCase();
  if (name.includes('natural') || name.includes('neural') || uri.includes('premium') || name.includes('(premium)')) return 5;
  if (uri.includes('enhanced') || name.includes('(enhanced)')) return 4;
  if (name.startsWith('google')) return 3;
  if (name.includes('espeak') || uri.includes('espeak')) return 0;
  if (name.startsWith('microsoft')) return 1;
  return 2;
}

/**
 * The narrator's voice among `voices`, for an article in `language` read in
 * `preferredRegion` (the locale's region code, so a French reader in Canada
 * gets a Canadian French voice when the device has one). Null when the
 * device has no voice in the language.
 */
export function pickNarratorVoice<V extends VoiceLike>(
  voices: readonly V[],
  language: string,
  preferredRegion?: string | null,
): V | null {
  const want = parseLangTag(language);
  if (!want.base) return null;
  const candidates = voices.filter((v) => parseLangTag(v.lang).base === want.base && !isNovelty(v));
  if (candidates.length === 0) return null;

  const regions: string[] = [];
  for (const r of [preferredRegion?.toUpperCase(), want.region, ...(REGION_FALLBACK[want.base] ?? [])]) {
    if (r && !regions.includes(r)) regions.push(r);
  }
  const preferred = (want.region && PREFERRED[`${want.base}-${want.region}`]) || PREFERRED[want.base] || [];
  const preferredWords = preferred.map(word);

  const score = (v: V): number => {
    const region = parseLangTag(v.lang).region;
    const regionRank = regions.indexOf(region);
    const regionScore = regionRank === -1 ? 0 : Math.max(0, regions.length - regionRank);
    const nameWords = word(v.name).split(/[\s(),-]+/).filter(Boolean);
    const full = word(v.name);
    let preferredScore = 0;
    for (let i = 0; i < preferredWords.length; i++) {
      const p = preferredWords[i];
      if (full === p || full.startsWith(`${p} `) || nameWords.includes(p)) {
        preferredScore = preferredWords.length - i;
        break;
      }
    }
    return voiceTier(v) * 1000 + regionScore * 50 + preferredScore + (v.default ? 0.5 : 0);
  };

  let best: V | null = null;
  let bestScore = -1;
  for (const v of candidates) {
    const s = score(v);
    if (s > bestScore) {
      best = v;
      bestScore = s;
    }
  }
  return best;
}
